import React, { useState } from 'react';
import { Modal, Tabs, Input, Button, Table, Form, Select, Tag, Alert, Space, message } from 'antd';
import { useMutation } from '@apollo/client';
import {
  BATCH_CREATE_I18N_CONFIG,
  BatchCreateI18nConfigResult,
  CreateI18nConfigInput,
  I18nConfigBatchFailure,
} from '../graphql/mutations/i18nConfigAdminMutations';
import { POPUP_TYPE_OPTIONS } from '../I18nConfigAdminPage';
import { showError } from 'utils/exceptionUtils';

const { TextArea } = Input;

const JSON_PLACEHOLDER = `[
  {
    "messageKey": "TMS_CARD_EXAMPLE_KEY",
    "popupType": 0,
    "triggerScene": "示例场景说明",
    "contentZh": "中文文案",
    "contentEn": "English text",
    "contentEs": "Texto en español"
  }
]`;

type PreviewRow = {
  index: number;
  messageKey: string;
  valid: boolean;
  reason?: string;
};

const validateShape = (item: any): string | null => {
  if (typeof item !== 'object' || item === null) return '不是一个对象';
  if (typeof item.messageKey !== 'string' || !item.messageKey.trim()) return 'messageKey 缺失';
  if (typeof item.popupType !== 'number') return 'popupType 缺失或不是数字';
  return null;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  /** 批量新增完成（无论是否全部成功）后回调，由外层决定是否关闭弹窗 + 刷新列表 */
  onDone: () => void;
};

const I18nConfigBatchCreateModal: React.FC<Props> = ({ visible, onClose, onDone }) => {
  const [jsonText, setJsonText] = useState('');
  const [preview, setPreview] = useState<PreviewRow[]>([]);
  const [parsedItems, setParsedItems] = useState<CreateI18nConfigInput[] | null>(null);
  const [tableForm] = Form.useForm<{ rows: CreateI18nConfigInput[] }>();
  const [result, setResult] = useState<BatchCreateI18nConfigResult | null>(null);
  const [batchCreate, { loading }] = useMutation(BATCH_CREATE_I18N_CONFIG);

  const reset = () => {
    setJsonText('');
    setPreview([]);
    setParsedItems(null);
    setResult(null);
    tableForm.resetFields();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleJsonPreview = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        message.error('JSON 必须是一个非空数组');
        return;
      }
      const rows: PreviewRow[] = parsed.map((item, index) => {
        const reason = validateShape(item);
        return { index, messageKey: item?.messageKey ?? '(未知)', valid: !reason, reason: reason ?? undefined };
      });
      setPreview(rows);
      setParsedItems(parsed as CreateI18nConfigInput[]);
    } catch (e) {
      message.error('JSON 解析失败，请检查格式');
      setPreview([]);
      setParsedItems(null);
    }
  };

  const submitItems = async (items: CreateI18nConfigInput[]) => {
    try {
      const { data } = await batchCreate({ variables: { items } });
      const batchResult: BatchCreateI18nConfigResult = data.batchCreateI18nConfig;
      setResult(batchResult);
      if (batchResult.failed.length === 0) {
        message.success(`全部 ${batchResult.succeeded.length} 条新增成功`);
        onDone();
      } else {
        message.warning(`成功 ${batchResult.succeeded.length} 条，失败 ${batchResult.failed.length} 条，详见下方列表`);
      }
    } catch (e: any) {
      showError(e);
    }
  };

  const handleJsonSubmit = () => {
    if (!parsedItems) {
      message.error('请先点击「校验预览」');
      return;
    }
    submitItems(parsedItems);
  };

  const handleTableSubmit = async () => {
    const values = await tableForm.validateFields();
    const rows = values.rows ?? [];
    if (rows.length === 0) {
      message.error('请至少添加一行');
      return;
    }
    submitItems(rows);
  };

  const failureColumns = [
    { title: 'messageKey', dataIndex: 'messageKey', key: 'messageKey' },
    { title: '失败原因', dataIndex: 'reason', key: 'reason' },
  ];

  const previewColumns = [
    { title: '#', dataIndex: 'index', key: 'index', width: 50 },
    { title: 'messageKey', dataIndex: 'messageKey', key: 'messageKey' },
    {
      title: '状态',
      key: 'status',
      width: 140,
      render: (_: any, row: PreviewRow) =>
        row.valid ? <Tag color="green">待提交</Tag> : <Tag color="red">{row.reason}</Tag>,
    },
  ];

  const renderResult = () =>
    result && (
      <Alert
        className="mt-3"
        type={result.failed.length === 0 ? 'success' : 'warning'}
        showIcon
        message={`成功 ${result.succeeded.length} 条，失败 ${result.failed.length} 条`}
        description={
          result.failed.length > 0 && (
            <Table<I18nConfigBatchFailure>
              className="mt-2"
              size="small"
              rowKey="messageKey"
              columns={failureColumns}
              dataSource={result.failed}
              pagination={false}
            />
          )
        }
      />
    );

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      width={900}
      title="批量新增文案配置"
      footer={null}
      destroyOnClose
    >
      <Tabs
        items={[
          {
            key: 'json',
            label: 'JSON 粘贴',
            children: (
              <div>
                <TextArea
                  value={jsonText}
                  onChange={(e) => {
                    setJsonText(e.target.value);
                    setParsedItems(null);
                    setPreview([]);
                  }}
                  placeholder={JSON_PLACEHOLDER}
                  autoSize={{ minRows: 10, maxRows: 16 }}
                  className="font-mono"
                />
                <div className="flex justify-end gap-2 mt-2">
                  <Button onClick={handleJsonPreview}>校验预览</Button>
                  <Button type="primary" loading={loading} disabled={!parsedItems} onClick={handleJsonSubmit}>
                    提交
                  </Button>
                </div>
                {preview.length > 0 && (
                  <Table<PreviewRow>
                    className="mt-3"
                    size="small"
                    rowKey="index"
                    columns={previewColumns}
                    dataSource={preview}
                    pagination={false}
                    scroll={{ y: 240 }}
                  />
                )}
              </div>
            ),
          },
          {
            key: 'table',
            label: '表格加行',
            children: (
              <div>
                <Form form={tableForm} initialValues={{ rows: [{}] }}>
                  <Form.List name="rows">
                    {(fields, { add, remove }) => (
                      <div className="overflow-x-auto">
                        <div style={{ minWidth: 960 }}>
                          {fields.map((field) => (
                            <Space key={field.key} align="start" className="flex mb-2" wrap={false}>
                              <Form.Item
                                {...field}
                                name={[field.name, 'messageKey']}
                                rules={[{ required: true, message: 'messageKey必填' }]}
                              >
                                <Input placeholder="messageKey" style={{ width: 220 }} />
                              </Form.Item>
                              <Form.Item
                                {...field}
                                name={[field.name, 'popupType']}
                                rules={[{ required: true, message: '必选' }]}
                              >
                                <Select
                                  placeholder="弹窗级别"
                                  options={POPUP_TYPE_OPTIONS}
                                  style={{ width: 140 }}
                                />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, 'triggerScene']}>
                                <Input placeholder="触发场景" style={{ width: 160 }} />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, 'contentZh']}>
                                <Input placeholder="正文-中文" style={{ width: 160 }} />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, 'contentEn']}>
                                <Input placeholder="正文-英文" style={{ width: 160 }} />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, 'contentEs']}>
                                <Input placeholder="正文-西语" style={{ width: 160 }} />
                              </Form.Item>
                              <Button danger type="link" onClick={() => remove(field.name)}>
                                删除
                              </Button>
                            </Space>
                          ))}
                        </div>
                        <Button onClick={() => add({})}>+ 新增一行</Button>
                      </div>
                    )}
                  </Form.List>
                </Form>
                <div className="text-xs text-gray-500 mt-2">
                  仅展示常用字段（标题/备注等不常用字段请用单条新建或 JSON 粘贴）
                </div>
                <div className="flex justify-end mt-2">
                  <Button type="primary" loading={loading} onClick={handleTableSubmit}>
                    提交
                  </Button>
                </div>
              </div>
            ),
          },
        ]}
      />
      {renderResult()}
    </Modal>
  );
};

export default I18nConfigBatchCreateModal;
